import * as XLSX from 'xlsx';
import { fmtTime } from './time.js';
import { traceItemLabel, traceSummary } from './trace.js';
import { workTypeLabel, workTypeOf } from './workTypes.js';

function round2(n) {
  return Math.round(n * 100) / 100;
}
function toMD(hours) {
  return round2(hours / 8);
}
function billablePct(hours, billableHours) {
  return hours ? Math.round((billableHours / hours) * 100) : 0;
}
function fname(prefix, from, to) {
  return `${prefix}_${from}_${to}.xlsx`;
}

function makeSheet(headers, rows, colWidths) {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  if (colWidths) ws['!cols'] = colWidths.map((w) => ({ wch: w }));
  return ws;
}

function save(wb, name) {
  XLSX.writeFile(wb, name);
}

export function exportEntries(entries, allProjects, { from, to }) {
  const headers = [
    'Date',
    'Person',
    'Code',
    'Project',
    'Client',
    'Work type',
    'Description',
    'Start',
    'End',
    'Hours',
    'MD',
  ];
  const rows = entries.map((entry) => {
    const project = allProjects.find((p) => p.id === entry.projectId);
    const hours = round2(entry.end - entry.start);
    return [
      entry.workDate,
      entry.userName,
      entry.projectCode,
      entry.projectName,
      project?.clientName || '-',
      workTypeLabel(workTypeOf(project, entry)),
      entry.description,
      fmtTime(entry.start),
      fmtTime(entry.end),
      hours,
      toMD(hours),
    ];
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(headers, rows, [12, 20, 8, 28, 20, 14, 42, 7, 7, 7, 7]),
    'Raw entries',
  );
  save(wb, fname('raw-entries', from, to));
}

export function exportProjects(rows, { from, to }) {
  const headers = [
    'Code',
    'Project',
    'Client',
    'Work type',
    'Hours',
    'MD',
    'Client work %',
    'People',
  ];
  const data = rows.map(
    ({ project, workType, hours, billableHours, peopleCount }) => [
      project.code,
      project.name,
      project.clientName,
      workTypeLabel(workType || workTypeOf(project)),
      round2(hours),
      toMD(hours),
      billablePct(hours, billableHours),
      peopleCount,
    ],
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(headers, data, [8, 30, 20, 14, 8, 8, 10, 8]),
    'By project',
  );
  save(wb, fname('by-project', from, to));
}

export function exportClients(rows, { from, to }) {
  const headers = [
    'Client',
    'Tier',
    'Hours',
    'MD',
    'Client work %',
    'Projects',
  ];
  const data = rows.map(({ client, hours, billableHours, projectCount }) => [
    client.name,
    client.tier || '-',
    round2(hours),
    toMD(hours),
    billablePct(hours, billableHours),
    projectCount,
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(headers, data, [25, 12, 8, 8, 10, 10]),
    'By client',
  );
  save(wb, fname('by-client', from, to));
}

// The By person table's columns: Total and MD are work plus paid absence,
// unpaid absence has its own column and is not in them; client work % is over
// work only.
const PEOPLE_SHEET_HEADERS = [
  'Name',
  'Team',
  'Total hours',
  'MD',
  'Client work hours',
  'Internal work hours',
  'Paid absence hours',
  'Unpaid absence hours (not in total)',
  'Client work %',
  'Projects',
];
const PEOPLE_SHEET_WIDTHS = [22, 16, 10, 8, 12, 12, 12, 16, 10, 10];

function peopleSheetRow({
  user,
  hours,
  workHours,
  billableHours,
  internalHours,
  paidAbsenceHours,
  unpaidAbsenceHours,
  projectCount,
}) {
  return [
    user.name,
    user.teamName || '-',
    round2(hours),
    toMD(hours),
    round2(billableHours || 0),
    round2(internalHours || 0),
    round2(paidAbsenceHours || 0),
    round2(unpaidAbsenceHours || 0),
    billablePct(workHours, billableHours),
    projectCount,
  ];
}

export function exportPeople(rows, { from, to }) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(
      PEOPLE_SHEET_HEADERS,
      rows.map(peopleSheetRow),
      PEOPLE_SHEET_WIDTHS,
    ),
    'By person',
  );
  save(wb, fname('by-person', from, to));
}

const TRACE_FIELD_LABEL = {
  projectCode: 'code',
  projectName: 'project',
  workDate: 'date',
  start: 'start',
  end: 'end',
  description: 'description',
};
function normDate(v) {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v))
    return v.slice(0, 10);
  return v ?? '';
}

export function exportTrace(logs, date) {
  const headers = [
    'Timestamp',
    'Actor',
    'Action',
    'Item',
    'Entry owner',
    'Project / client',
    'Work date',
    'Start',
    'End',
    'Description',
    'Changes',
  ];
  const rows = logs.map((log) => {
    const d = log.details || {};
    const isTimeEntry = log.entityType === 'time_entry';
    const isUpdate = log.action === 'update';
    const src = isTimeEntry && isUpdate ? d.after || {} : d;
    const context = d.clientName
      ? d.clientName
      : [src.projectCode, src.projectName].filter(Boolean).join(' ');
    const workDate = normDate(src.workDate);
    let changes = '';
    if (isTimeEntry && isUpdate) {
      changes = Object.keys(d.after || {})
        .filter((k) => normDate(d.before?.[k]) !== normDate(d.after?.[k]))
        .map(
          (k) =>
            `${TRACE_FIELD_LABEL[k] || k}: ${normDate(d.before?.[k])} → ${normDate(d.after?.[k])}`,
        )
        .join('; ');
    } else if (!isTimeEntry) {
      changes = traceSummary(log.entityType, d);
    }
    return [
      new Date(log.ts).toLocaleString(),
      log.actorName,
      log.action,
      traceItemLabel(log.entityType),
      d.entryOwnerName || '',
      context,
      workDate,
      src.start || '',
      src.end || '',
      src.description || '',
      changes,
    ];
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(headers, rows, [20, 20, 8, 18, 20, 30, 12, 8, 8, 40, 60]),
    'Trace',
  );
  save(wb, `trace_${date}.xlsx`);
}

export function exportOverview(
  summary,
  projectRows,
  clientRows,
  peopleRows,
  { from, to },
) {
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(
      ['Metric', 'Value'],
      [
        ['Period', `${from} to ${to}`],
        ['Total entries', summary.entryCount],
        ['Total hours (work + paid absence)', round2(summary.totalHours)],
        ['Total MD', toMD(summary.totalHours)],
        ['Client work hours', round2(summary.billableHours)],
        ['Internal work hours', round2(summary.internalHours || 0)],
        ['Paid absence hours', round2(summary.paidAbsenceHours || 0)],
        [
          'Unpaid absence hours (not in total)',
          round2(summary.unpaidAbsenceHours || 0),
        ],
        ['Client work % (of work)', Math.round(summary.billablePct)],
        ['Projects', summary.projectCount],
        ['People', summary.userCount],
        ['Clients', summary.clientCount],
      ],
      [34, 16],
    ),
    'Summary',
  );

  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(
      ['Code', 'Project', 'Client', 'Hours', 'MD', 'People'],
      projectRows.map(({ project, hours, peopleCount }) => [
        project.code,
        project.name,
        project.clientName,
        round2(hours),
        toMD(hours),
        peopleCount,
      ]),
      [8, 30, 20, 8, 8, 8],
    ),
    'Projects',
  );

  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(
      ['Client', 'Hours', 'MD', 'Client work %', 'Projects'],
      clientRows.map(({ client, hours, billableHours, projectCount }) => [
        client.name,
        round2(hours),
        toMD(hours),
        billablePct(hours, billableHours),
        projectCount,
      ]),
      [25, 8, 8, 10, 10],
    ),
    'Clients',
  );

  XLSX.utils.book_append_sheet(
    wb,
    makeSheet(
      PEOPLE_SHEET_HEADERS,
      peopleRows.map(peopleSheetRow),
      PEOPLE_SHEET_WIDTHS,
    ),
    'People',
  );

  save(wb, fname('overview', from, to));
}
